import { useEffect, useMemo, useState } from 'react'
import { useT } from '../i18n'
import { ConfettiBurst } from './ConfettiBurst'

/**
 * HAPPY <DAY> greeting on the holidays the games are themed for
 * (owner call 2026-08-20): a once-per-day overlay with confetti firing
 * in from both side edges, colored to match the day. The app stays
 * visible and playable behind it; a tap or five seconds clears it.
 *
 * Dates use the DEVICE's calendar, not word_game_day: a greeting
 * should match the date on the player's own clock. Thanksgiving is
 * the fourth Thursday of November; everything else is fixed.
 *
 * The seen-stamp is decided in the state initializer but only WRITTEN
 * in the effect: StrictMode double-invokes initializers, and a write
 * there would make the second pass read its own stamp and hide the
 * greeting (the avatar-crop lesson).
 */

interface Holiday {
  key: string
  label: string
  /** Big text color. Dark enough for the off-white background. */
  color: string
  /** Confetti piece colors. */
  palette: string[]
}

function thanksgivingDay(year: number): number {
  // Fourth Thursday of November.
  const first = new Date(year, 10, 1).getDay()
  const firstThursday = 1 + ((4 - first + 7) % 7)
  return firstThursday + 21
}

export function holidayFor(d: Date): Holiday | null {
  const m = d.getMonth() + 1
  const day = d.getDate()
  if (m === 1 && day === 1) return {
    key: 'new_year', label: 'Happy New Year!', color: '#A16207',
    palette: ['#F5D06F', '#E8B923', '#C9A227', '#FFFFFF'],
  }
  if (m === 2 && day === 14) return {
    key: 'valentines', label: "Happy Valentine's Day!", color: '#C2185B',
    palette: ['#E0447C', '#F06292', '#F8BBD0', '#C2185B'],
  }
  if (m === 3 && day === 17) return {
    key: 'stpatricks', label: "Happy St. Patrick's Day!", color: '#1B5E20',
    palette: ['#2E7D32', '#66BB6A', '#A5D6A7', '#1B5E20'],
  }
  if (m === 7 && day === 4) return {
    key: 'july4', label: 'Happy Fourth of July!', color: '#1A47B8',
    palette: ['#B71C1C', '#FFFFFF', '#1A47B8', '#E53935'],
  }
  if (m === 10 && day === 31) return {
    key: 'halloween', label: 'Happy Halloween!', color: '#E65100',
    palette: ['#E65100', '#FF9800', '#4A148C', '#212121'],
  }
  if (m === 11 && day === thanksgivingDay(d.getFullYear())) return {
    key: 'thanksgiving', label: 'Happy Thanksgiving!', color: '#B45309',
    palette: ['#B45309', '#D97706', '#EA580C', '#92400E'],
  }
  if (m === 12 && day === 25) return {
    key: 'christmas', label: 'Merry Christmas!', color: '#B91C1C',
    palette: ['#B91C1C', '#15803D', '#FCD34D', '#FFFFFF'],
  }
  if (m === 12 && day === 31) return {
    key: 'nye', label: "Happy New Year's Eve!", color: '#A16207',
    palette: ['#E8B923', '#C0C0C0', '#F5D06F', '#0F172A'],
  }
  return null
}

function localStamp(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `jdg_holiday_greet_${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export function HolidayGreeting({ now }: { now?: Date }) {
  const t = useT()
  const today = useMemo(() => now ?? new Date(), [now])
  const holiday = useMemo(() => holidayFor(today), [today])

  // Decide visibility once, without writing the stamp (StrictMode).
  const [show, setShow] = useState<boolean>(() => {
    if (!holiday) return false
    try { return localStorage.getItem(localStamp(today)) === null } catch { return true }
  })

  useEffect(() => {
    if (!show || !holiday) return
    try { localStorage.setItem(localStamp(today), '1') } catch { /* private mode */ }
    const timer = setTimeout(() => setShow(false), 5000)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, holiday?.key])

  if (!show || !holiday) return null

  return (
    <div
      className="fixed inset-0 z-[120] overflow-hidden"
      style={{ pointerEvents: 'none' }}
      aria-live="polite"
    >
      <ConfettiBurst palette={holiday.palette} />
      <div className="absolute inset-x-0 top-1/3 flex justify-center px-6">
        <button
          type="button"
          onClick={() => setShow(false)}
          className="rounded-3xl bg-white/90 border-2 border-brand-border shadow-float
                     px-8 py-5 text-center backdrop-blur-sm active:scale-[0.98]
                     transition-transform animate-[scaleIn_0.35s_ease-out]"
          style={{ pointerEvents: 'auto' }}
        >
          <p className="font-display font-black text-3xl uppercase tracking-wide"
             style={{ color: holiday.color }}>
            {t(holiday.label)}
          </p>
        </button>
      </div>
    </div>
  )
}
